import { useEffect, useRef, useState, useCallback } from "react";
import { useOakPad } from "@/context/OakPadContext";
import { Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * HighlightToClip — *true* auto-clip on selection.
 *
 * Watches `pointerup` / `touchend` / `keyup` (for Shift+arrow keyboard
 * selections) on the document. Whenever the user releases with a valid
 * ≥4-char selection fully inside the wrapped region, the snippet is
 * pushed straight into OakPad. A floating "Clipped ✓ Undo" pill appears
 * near the selection for 3 seconds; clicking Undo removes it from the
 * pad (and from the server). After 3s the pill fades out on its own.
 *
 * The pill is no longer a *CTA* — it is a confirmation + escape hatch.
 * Matches the coachmark wording: "Highlight any line — it auto-clips".
 */
const PILL_TTL_MS = 3000;
const MIN_CHARS = 4;

export default function HighlightToClip({ children, pathId, className }) {
  const { addSnippet, removeSnippet } = useOakPad();
  const containerRef = useRef(null);

  // confirmation pill state — { x, y, clipId, fading }
  const [pill, setPill] = useState(null);
  // tracks the most recently clipped selection so a stationary cursor
  // doesn't spam re-clips of the same range.
  const lastClipRef = useRef({ text: "", at: 0 });
  const fadeTimerRef = useRef(null);

  const dismissPill = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    setPill(null);
  }, []);

  const undoClip = useCallback((clipId) => {
    if (!clipId) return;
    removeSnippet(clipId);
    dismissPill();
    toast("Removed from OAK-Pad", { duration: 1500 });
  }, [removeSnippet, dismissPill]);

  // Capture the active selection. Returns null if it's not eligible.
  const tryCaptureSelection = useCallback(() => {
    const sel = window.getSelection?.();
    if (!sel || sel.isCollapsed || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    const root = containerRef.current;
    if (!root || !root.contains(range.commonAncestorContainer)) return null;
    const text = String(sel).trim();
    if (text.length < MIN_CHARS) return null;
    // Same exact text within 800ms? Don't double-clip.
    const now = Date.now();
    if (text === lastClipRef.current.text && now - lastClipRef.current.at < 800) {
      return null;
    }
    const rect = range.getBoundingClientRect();
    return {
      text,
      x: rect.left + rect.width / 2,
      y: Math.max(48, rect.top - 8),
    };
  }, []);

  const performAutoClip = useCallback(() => {
    const cap = tryCaptureSelection();
    if (!cap) return;
    const snip = addSnippet(cap.text, { path_id: pathId, title: "Highlighted clip" });
    if (!snip) return;
    lastClipRef.current = { text: cap.text, at: Date.now() };

    // Toast (consistent with old pill-click flow). Sonner already lives globally.
    toast.success("Clipped to OAK-Pad", {
      description: cap.text.length > 100 ? cap.text.slice(0, 100) + "…" : cap.text,
    });

    // Show the "✓ Clipped — Undo?" confirmation pill
    setPill({ x: cap.x, y: cap.y, clipId: snip.id });

    // Collapse the selection so the highlight doesn't linger ghost-like
    try { window.getSelection?.()?.removeAllRanges?.(); } catch {}

    // Auto-fade after TTL
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setPill(null);
      fadeTimerRef.current = null;
    }, PILL_TTL_MS);
  }, [addSnippet, pathId, tryCaptureSelection]);

  // Detect "selection has stabilised" via debounced `selectionchange`.
  // Radix Dialog can swallow pointer/mouseup bubbling, but `selectionchange`
  // fires at the document level on every selection edit (including the
  // FINAL state after a drag ends) and isn't impeded by Radix portals.
  // We wait 350ms after the last selectionchange before auto-clipping —
  // that's long enough that a still-dragging selection won't trigger it,
  // and short enough to feel instant when the user lifts the mouse.
  useEffect(() => {
    let timer = null;
    const onSelectionChange = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        performAutoClip();
      }, 350);
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [performAutoClip]);

  // Cleanup any pending fade timer on unmount
  useEffect(() => () => {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
  }, []);

  return (
    <div ref={containerRef} className={className} data-testid="highlight-to-clip">
      {children}
      {pill && (
        <div
          style={{
            position: "fixed",
            left: pill.x,
            top: pill.y,
            transform: "translate(-50%, -100%)",
            zIndex: 80,
            pointerEvents: "auto",
          }}
          className="inline-flex items-center gap-2 rounded-full pl-2 pr-1 py-1 text-[11px] font-display font-semibold border border-[#00FF9D]/45 bg-[#0A192F] text-[#00FF9D] shadow-[0_8px_24px_-8px_rgba(0,255,157,0.45)] animate-fadeup"
          data-testid="highlight-clip-confirm"
        >
          <span className="inline-flex items-center gap-1.5 px-1">
            <Check size={12} /> Clipped to OAK-Pad
          </span>
          <button
            type="button"
            onClick={() => undoClip(pill.clipId)}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] uppercase tracking-wider text-white/65 hover:text-white hover:bg-white/10 transition"
            data-testid="highlight-clip-undo"
          >
            <RotateCcw size={10} /> Undo
          </button>
        </div>
      )}
    </div>
  );
}
